// src/AuditChecklistPage.js — the audit checklist, as its own screen.
//
// This used to be a second tab on System Settings. Nobody found it there, which
// is a fair outcome for hiding a security checklist behind a page about session
// timeouts. The content still comes from /api/owner-checklist and is still
// owner-gated on the server; only where you click has changed.
//
// When the server refuses, this says why. The old behaviour was to render
// nothing at all, so a misconfigured OWNER_EMAILS and "you are not the owner"
// looked identical from the browser: no tab, no error, nothing to search for.
import React, { useEffect, useState } from 'react';
import { Alert, AlertTitle, Box, CircularProgress, Typography } from '@mui/material';
import OwnerAuditChecklist, { loadOwnerChecklist } from './OwnerAuditChecklist';

function AuditChecklistPage() {
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    let live = true;
    loadOwnerChecklist().then((data) => {
      if (live) setState({ loading: false, data });
    });
    return () => { live = false; };
  }, []);

  if (state.loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const { data } = state;

  if (data && data.sections) {
    return (
      <Box sx={{ p: 2 }}>
        {/* The checklist, and nothing above it. A dashboard of donuts and
            tiles sat here briefly and was removed: this page is for working
            through items, and anything in front of them is in the way. The
            one summary line the list carries is enough. */}
        <OwnerAuditChecklist initial={data} />
      </Box>
    );
  }

  // null means the server said "not the owner" without a reason it could
  // describe. Everything else carries an explanation worth showing.
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" sx={{ mb: 2 }}>Audit Checklist</Typography>
      <Alert severity={data ? 'warning' : 'info'}>
        <AlertTitle>{data ? 'Not loaded' : 'Owner access only'}</AlertTitle>
        {data
          ? (
            <>
              {data.error}
              {data.hint ? <Box sx={{ mt: 1 }}>{data.hint}</Box> : null}
            </>
          )
          : 'This checklist is limited to the account listed as the owner. If that should be you, check OWNER_EMAILS in Netlify.'}
      </Alert>
    </Box>
  );
}

export default AuditChecklistPage;
